// Shared envelope + payload types for the evisio2 ↔ inner-webtool postMessage bridge.
// Both EvisioBridgeHost (parent) and EvisioBridgeClient (child) import from here so the
// wire format stays in lockstep.

export type Theme = "light" | "dark" | "auto";

export type EvisioBridgeCommandName =
    | "setTheme"
    | "openEntityView"
    | "highlightCalendarOverview"
    | "openEmailEditor"
    // Editor-scoped — only routable while the email editor dialog is open.
    // Tools backing these are registered/unregistered on the AI Connection at open/close
    // via BRIDGE_TOPIC.EmailEditorOpened / EmailEditorClosed (Evisio2SdkWiring).
    | "getEmailDraft"
    | "setEmailDraft"
    | "appendEmailBody"
    | "listEmailAttachments"
    | "getEmailAttachment";

export interface ReadyPayload {
    // window.currentLanguageCode at child load time. Reporting only — bridge never drives language.
    languageCode: string;
    // Stored preference (getTheme(false)), not resolved visible class.
    theme: Theme;
    currentPath: string;
    currentSearch: string;
    currentHash: string;
    // Commands the current user can actually invoke (roles + features evaluated on the child).
    // Computed once when the child emits ready — ready emission is deferred until roles are loaded.
    availableCommands: EvisioBridgeCommandName[];
}

export interface ThemeChangedPayload {
    theme: Theme;
}

export interface UrlChangedPayload {
    path: string;
    search: string;
    hash: string;
}

export interface SetThemePayload {
    theme: Theme;
}

export interface OpenEntityViewPayload {
    entityId: string;
    // Resolved server-side via Evisio2__Helpers_TrySearchClassnameByEntityId when omitted.
    entityClass?: string;
    label?: string;
    additionalParams?: any;
}

export interface HighlightCalendarOverviewPayload {
    // Expected to be an ev_booking ID; validated server-side via Evisio2__Helpers_IsEvBookingId.
    entityId: string;
}

export interface OpenEmailEditorPayload {
    subject?: string;
    htmlBody?: string;        // used as-is (sanitized by the editor's HtmlEditor / DOMPurify)
    plainTextBody?: string;   // escaped + \n→<br> when htmlBody is absent
    to?: string[];
    cc?: string[];
    bcc?: string[];
    // Optional context entity the mail relates to. BOTH must be set and valid
    // (checked via _Evisio2__ValidateClassAndId); a partial pair is ignored.
    contextClass?: string;
    contextId?: string;
}

// Editor-scoped commands. All require an open editor session — the wrapper
// (evisioEmailEditor) rejects with editorNotOpen if the singleton slot is empty.

export interface GetEmailDraftPayload { /* no params */ }
export interface GetEmailDraftResult {
    subject: string;
    bodyHtml: string;
    // Plain string lists (one address per entry). Internal mailRecipient objects with
    // entityID are flattened to mailaddress strings before crossing the bridge.
    to: string[];
    cc: string[];
    bcc: string[];
}

export interface SetEmailDraftPayload {
    // Field-replace semantics: each field passed fully overwrites; omitted fields
    // are untouched. Clear a field by passing the empty string. Recipients are
    // intentionally NOT writable here — agent reads via getEmailDraft only.
    subject?: string;
    bodyHtml?: string;
}
export interface SetEmailDraftResult { ok: true; }

export interface AppendEmailBodyPayload {
    html: string;
}
export interface AppendEmailBodyResult { ok: true; }

export interface EmailAttachmentInfo {
    // Universal identifier — for regular attachments this is the file's GUID (also the
    // descriptor download key, see filePreview.ts comment); for rawFileAttachments this
    // is the in-memory fileID. The bridge handler disambiguates internally.
    id: string;
    filename: string;
    mime?: string;
}
export interface ListEmailAttachmentsPayload { /* no params */ }
export interface ListEmailAttachmentsResult {
    attachments: EmailAttachmentInfo[];
}

export interface GetEmailAttachmentPayload {
    id: string;
}
export interface GetEmailAttachmentResult {
    name: string;
    mime: string;
    // Base64-encoded bytes (no data-URI prefix). The wiring side decodes to ArrayBuffer
    // before wrapping in EvisioFileResult — base64 in transit avoids structured-clone
    // surprises across the postMessage boundary.
    base64: string;
}

// Lifecycle events emitted by the eGo iframe (via the evisioEmailEditor wrapper) so
// the parent-side SDK wiring can register/unregister editor-scoped tools per session.
export interface EmailEditorOpenedPayload { /* reserved for future fields */ }
export interface EmailEditorClosedPayload { /* reserved for future fields */ }

export type EvisioBridgeErrorKind =
    | "unknownEntity"
    | "notABooking"
    | "roleDenied"
    | "widgetNotFound"
    | "backendError"
    | "handlerThrew"
    | "editorAlreadyOpen"
    | "editorNotOpen"
    | "attachmentNotFound"
    | "invalidParams";

export interface EvisioBridgeErrorShape {
    kind: EvisioBridgeErrorKind;
    message: string;
}

export interface CommandResultPayload {
    requestId: string;
    ok: boolean;
    error?: EvisioBridgeErrorShape;
    result?: any;
}

// Optional correlation id — commands that need a result set it; fire-and-forget commands leave it undefined.
export interface CommandMeta {
    requestId?: string;
}

// Events flow child → parent.
export type EvisioBridgeEvent =
    | { kind: "evisio2:event"; type: "ready";              payload: ReadyPayload }
    | { kind: "evisio2:event"; type: "themeChanged";       payload: ThemeChangedPayload }
    | { kind: "evisio2:event"; type: "urlChanged";         payload: UrlChangedPayload }
    | { kind: "evisio2:event"; type: "commandResult";      payload: CommandResultPayload }
    | { kind: "evisio2:event"; type: "emailEditorOpened";  payload: EmailEditorOpenedPayload }
    | { kind: "evisio2:event"; type: "emailEditorClosed";  payload: EmailEditorClosedPayload };

// Commands flow parent → child.
export type EvisioBridgeCommand =
    | { kind: "evisio2:command"; type: "setTheme";                  meta?: CommandMeta; payload: SetThemePayload }
    | { kind: "evisio2:command"; type: "openEntityView";            meta?: CommandMeta; payload: OpenEntityViewPayload }
    | { kind: "evisio2:command"; type: "highlightCalendarOverview"; meta?: CommandMeta; payload: HighlightCalendarOverviewPayload }
    | { kind: "evisio2:command"; type: "openEmailEditor";           meta?: CommandMeta; payload: OpenEmailEditorPayload }
    | { kind: "evisio2:command"; type: "getEmailDraft";             meta?: CommandMeta; payload: GetEmailDraftPayload }
    | { kind: "evisio2:command"; type: "setEmailDraft";             meta?: CommandMeta; payload: SetEmailDraftPayload }
    | { kind: "evisio2:command"; type: "appendEmailBody";           meta?: CommandMeta; payload: AppendEmailBodyPayload }
    | { kind: "evisio2:command"; type: "listEmailAttachments";      meta?: CommandMeta; payload: ListEmailAttachmentsPayload }
    | { kind: "evisio2:command"; type: "getEmailAttachment";        meta?: CommandMeta; payload: GetEmailAttachmentPayload };

export type EvisioBridgeMessage = EvisioBridgeEvent | EvisioBridgeCommand;

// Parent-side command queue metadata. Stale commands are dropped on next ready flush.
export interface QueuedCommand {
    cmd: EvisioBridgeCommand;
    enqueuedAt: number;
    survivesReload: boolean;
}

export const BRIDGE_NAMESPACE_EVENT = "evisio2:event";
export const BRIDGE_NAMESPACE_COMMAND = "evisio2:command";
export const COMMAND_STALE_MS = 10000;

// Hardcoded final-fallback timeout on every awaited command. If the child never replies
// (crashed handler, navigation race, dropped postMessage), the host rejects the pending
// request with `handlerThrew` after this many seconds — no request can hang forever.
// Generous because highlightCalendarOverview can navigate + reload + poll on the child.
export const COMMAND_RESULT_TIMEOUT_SECONDS = 30;

// Dojo topic names — the bridge re-publishes incoming events onto these so existing
// widgets / future subscribers can listen without knowing about postMessage.
export const BRIDGE_TOPIC = {
    Ready:               "/Evisio2Bridge/Event/Ready",
    UrlChanged:          "/Evisio2Bridge/Event/UrlChanged",
    ThemeChanged:        "/Evisio2Bridge/Event/ThemeChanged",
    // Emitted child-side by evisioEmailEditor on open, then relayed to parent. Parent's
    // EvisioBridgeHost re-publishes on this same string so Evisio2SdkWiring can listen.
    EmailEditorOpened:   "/Evisio2Bridge/Event/EmailEditorOpened",
    EmailEditorClosed:   "/Evisio2Bridge/Event/EmailEditorClosed",
} as const;
